// src/ClientInfo.js
import ScrollToTop from './ScrollToTop';

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './ProgressBar.css';
import { useQuote } from './QuoteContext';
import { motion } from 'framer-motion';
import { Button } from "./components/ui/button";

const SENEGAL_MOBILE_RE = /^(\+221|00221)?\s?7[0-9]\s?\d{3}\s?\d{2}\s?\d{2}$/;
const SENEGAL_FIXE_RE   = /^(\+221|00221)?\s?33\s?\d{3}\s?\d{2}\s?\d{2}$/;
const GENERIC_PHONE_RE  = /^\+?[\d\s\-().]{7,20}$/;
const EMAIL_RE          = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

const normalizePhone = (v) => v.replace(/[\s\-().]/g, '');

const validatePhone = (value, fieldName) => {
  if (!value || value.trim() === '') return '';
  const clean = normalizePhone(value);
  if (fieldName === 'mobile') {
    if (SENEGAL_MOBILE_RE.test(value) || /^7[0-9]\d{7}$/.test(clean)) return '';
    if (GENERIC_PHONE_RE.test(value)) return '';
    return 'Format invalide. Ex : 77 123 45 67 ou +221 77 123 45 67';
  }
  if (fieldName === 'phone') {
    if (SENEGAL_FIXE_RE.test(value) || /^33\d{7}$/.test(clean)) return '';
    if (GENERIC_PHONE_RE.test(value)) return '';
    return 'Format invalide. Ex : 33 123 45 67 ou +221 33 123 45 67';
  }
  return '';
};

const ClientInfo = ({ currentStep, steps }) => {
  const navigate = useNavigate();
  const { updateQuoteData, quoteData } = useQuote();

  const [formData, setFormData] = useState({
    clientName: '',
    region: 'Sénégal',
    attentionTitle: '',
    attentionName: '',
    mobile: '',
    phone: '',
    email: '',
    emailsCc: [],
    address: '',
    poNumber: generateRandomPO(),
  });

  const [errors, setErrors] = useState({
    clientName: '',
    mobile: '',
    phone: '',
    email: '',
  });

  const [touched, setTouched] = useState({});
  const [showPreview, setShowPreview] = useState(false);
  const [ccInput, setCcInput] = useState('');
  const [ccError, setCcError] = useState('');

  useEffect(() => {
    if (quoteData.clientInfo) {
      setFormData(prev => ({
        ...prev,
        clientName: '',
        region: 'Sénégal',
        attentionTitle: '',
        attentionName: '',
        mobile: '',
        phone: '',
        email: '',
        emailsCc: [],
        address: '',
        ...quoteData.clientInfo,
      }));
    } else {
      setFormData(prev => ({ ...prev, poNumber: generateRandomPO() }));
    }
  }, [quoteData.clientInfo]);

  function generateRandomPO() {
    return 'PO' + Math.floor(100000 + Math.random() * 900000);
  }

  const validateField = (name, value) => {
    const v = value ?? '';
    switch (name) {
      case 'clientName':
        return v.trim().length < 2 ? 'Le nom doit contenir au moins 2 caractères' : '';
      case 'mobile':
        return validatePhone(v, 'mobile');
      case 'phone':
        return validatePhone(v, 'phone');
      case 'email':
        if (!v || v.trim() === '') return '';
        return EMAIL_RE.test(v.trim()) ? '' : 'Adresse email invalide. Ex : exemple@domaine.com';
      default:
        return '';
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    let newValue = value;
    if (name === 'clientName' || name === 'attentionName') {
      newValue = value.toUpperCase();
    }
    setFormData(prev => ({ ...prev, [name]: newValue }));
    if (touched[name]) {
      setErrors(prev => ({ ...prev, [name]: validateField(name, newValue) }));
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    setTouched(prev => ({ ...prev, [name]: true }));
    setErrors(prev => ({ ...prev, [name]: validateField(name, value) }));
  };

  const validateAll = () => {
    const fields = ['clientName', 'mobile', 'phone', 'email'];
    const newErrors = {};
    let isValid = true;
    fields.forEach(f => {
      const err = validateField(f, formData[f]);
      newErrors[f] = err;
      if (err) isValid = false;
    });
    setErrors(prev => ({ ...prev, ...newErrors }));
    setTouched({ clientName: true, mobile: true, phone: true, email: true });
    return isValid;
  };

  const handleAddCc = () => {
    const v = ccInput.trim();
    if (!v) return;
    if (!EMAIL_RE.test(v)) { setCcError('Adresse email invalide'); return; }
    if ((formData.emailsCc || []).includes(v)) { setCcError('Email déjà ajouté'); return; }
    setFormData(prev => ({ ...prev, emailsCc: [...(prev.emailsCc || []), v] }));
    setCcInput('');
    setCcError('');
  };

  const handleRemoveCc = (idx) => {
    setFormData(prev => ({ ...prev, emailsCc: prev.emailsCc.filter((_, i) => i !== idx) }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (validateAll()) {
      updateQuoteData({ clientInfo: formData });
      navigate('/company-info');
    }
  };

  const handleSave = () => {
    if (validateAll()) {
      updateQuoteData({ clientInfo: formData });
    }
  };

  const handleReset = () => {
    setFormData({
      clientName: '',
      region: 'Sénégal',
      attentionTitle: '',
      attentionName: '',
      mobile: '',
      phone: '',
      email: '',
      emailsCc: [],
      address: '',
      poNumber: generateRandomPO(),
    });
    setErrors({ clientName: '', mobile: '', phone: '', email: '' });
    setTouched({});
    setCcInput('');
    setCcError('');
  };

  const fieldClass = (name) =>
    `w-full border rounded p-2 focus:outline-none focus:ring-2 ${
      errors[name] && touched[name]
        ? 'border-red-400 focus:ring-red-300'
        : 'border-gray-300 focus:ring-blue-300'
    }`;

  const ErrorMsg = ({ name }) =>
    errors[name] && touched[name] ? (
      <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
        <span>⚠</span> {errors[name]}
      </p>
    ) : null;

  return (
    <>
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
    >
      <div className="flex justify-center items-center min-h-screen bg-gray-100">
        <div className="bg-white p-6 rounded-lg shadow-lg max-w-3xl w-full">
          <form onSubmit={handleSubmit} className="space-y-4" noValidate>

            {/* Nom du client */}
            <div className="text-left">
              <label htmlFor="clientName" className="block font-semibold mb-1">
                Nom du client <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="clientName"
                name="clientName"
                value={formData.clientName}
                onChange={handleChange}
                onBlur={handleBlur}
                className={fieldClass('clientName')}
                required
              />
              <ErrorMsg name="clientName" />
            </div>

            {/* À l'attention de */}
            <div className="text-left">
              <label htmlFor="attentionTitle" className="block font-semibold mb-1">
                À l'attention de <span className="text-red-500">*</span>
              </label>
              <div className="flex space-x-2">
                <select
                  id="attentionTitle"
                  name="attentionTitle"
                  value={formData.attentionTitle}
                  onChange={handleChange}
                  className="border border-gray-300 rounded p-2 w-1/4 focus:outline-none focus:ring-2 focus:ring-blue-300"
                  required
                >
                  <option value="">Sélectionnez</option>
                  <option value="M.">M.</option>
                  <option value="MME">MME</option>
                </select>
                <input
                  type="text"
                  id="attentionName"
                  name="attentionName"
                  value={formData.attentionName}
                  onChange={handleChange}
                  placeholder="Nom"
                  className="w-3/4 border border-gray-300 rounded p-2 focus:outline-none focus:ring-2 focus:ring-blue-300"
                  required
                />
              </div>
            </div>

            {/* Tel. Mobile */}
            <div className="text-left">
              <label htmlFor="mobile" className="block font-semibold mb-1">
                Tél. Mobile du client <span className="text-red-500">*</span>
              </label>
              <input
                type="tel"
                id="mobile"
                name="mobile"
                value={formData.mobile}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="Ex : 77 123 45 67 ou +221 78 123 45 67"
                className={fieldClass('mobile')}
                required
              />
              <ErrorMsg name="mobile" />
              {!errors.mobile && touched.mobile && formData.mobile && (
                <p className="text-green-600 text-xs mt-1">✓ Numéro valide</p>
              )}
            </div>

            {/* Tel. Fixe */}
            <div className="text-left">
              <label htmlFor="phone" className="block font-semibold mb-1">
                Tél. Fixe du client
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="Ex : 33 123 45 67 ou +221 33 123 45 67"
                className={fieldClass('phone')}
              />
              <ErrorMsg name="phone" />
              {!errors.phone && touched.phone && formData.phone && (
                <p className="text-green-600 text-xs mt-1">✓ Numéro valide</p>
              )}
            </div>

            {/* Email */}
            <div className="text-left">
              <label htmlFor="email" className="block font-semibold mb-1">
                Email du client
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="Ex : client@exemple.com"
                className={fieldClass('email')}
              />
              <ErrorMsg name="email" />
              {!errors.email && touched.email && formData.email && (
                <p className="text-green-600 text-xs mt-1">✓ Email valide</p>
              )}
            </div>

            {/* Emails en copie (CC) */}
            <div className="text-left">
              <label className="block font-semibold mb-1">
                Emails en copie (CC)
                <span className="text-xs text-gray-400 font-normal ml-2">optionnel — recevront aussi le devis</span>
              </label>
              {(formData.emailsCc || []).length > 0 && (
                <div className="mb-2 space-y-1">
                  {(formData.emailsCc || []).map((cc, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <span className="flex-1 text-sm bg-blue-50 border border-blue-200 rounded px-3 py-1.5 text-blue-700">
                        📧 {cc}
                      </span>
                      <button
                        type="button"
                        onClick={() => handleRemoveCc(idx)}
                        className="text-red-400 hover:text-red-600 text-xl font-bold px-2 leading-none"
                        title="Supprimer"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <div className="flex gap-2">
                <input
                  type="email"
                  value={ccInput}
                  onChange={e => { setCcInput(e.target.value); setCcError(''); }}
                  onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleAddCc(); } }}
                  placeholder="Ex : directeur@entreprise.com"
                  className="flex-1 border border-gray-300 rounded p-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
                />
                <button
                  type="button"
                  onClick={handleAddCc}
                  className="px-4 bg-blue-600 text-white rounded hover:bg-blue-700 font-medium whitespace-nowrap" style={{height:"38px"}}
                >
                  + Ajouter
                </button>
              </div>
              {ccError && (
                <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                  <span>⚠</span> {ccError}
                </p>
              )}
              <div className="mt-2 flex items-center gap-2 bg-blue-50 border border-blue-200 rounded px-3 py-2">
                <span className="text-blue-500 text-base">ℹ️</span>
                <p className="text-xs text-blue-600">
                  <span className="font-semibold">contact@takoussane.com</span> est automatiquement en copie de tous les envois (devis, relances, factures).
                </p>
              </div>
            </div>

            {/* Ville / Localité */}
            <div className="text-left">
              <label htmlFor="address" className="block font-semibold mb-1">
                Ville / Localité <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="address"
                name="address"
                value={formData.address}
                onChange={handleChange}
                placeholder="Ex : Thiès, Rufisque, Ziguinchor..."
                className="w-full border border-gray-300 rounded p-2 focus:outline-none focus:ring-2 focus:ring-blue-300"
                required
              />
            </div>

            {/* Pays */}
            <div className="text-left">
              <label htmlFor="region" className="block font-semibold mb-1">
                Pays <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="region"
                name="region"
                value={formData.region}
                onChange={handleChange}
                placeholder="Ex : Sénégal, Espagne, USA..."
                className="w-full border border-gray-300 rounded p-2 focus:outline-none focus:ring-2 focus:ring-blue-300"
                required
              />
            </div>

            {/* Numéro PO */}
            <div className="text-left">
              <label htmlFor="poNumber" className="block font-semibold mb-1">Numéro client PO :</label>
              <input
                type="text"
                id="poNumber"
                name="poNumber"
                value={formData.poNumber}
                className="w-full border rounded p-2 bg-gray-100 text-gray-500"
                readOnly
              />
            </div>

            {/* Boutons */}
            <div className="flex justify-between items-center mt-8">
              <div className="flex gap-2 flex-wrap">
                <Button
                  type="button"
                  onClick={() => navigate('/')}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
                >
                  Accueil
                </Button>
                <Button
                  type="button"
                  onClick={handleSave}
                  className="bg-white border border-blue-500 text-blue-500 rounded hover:bg-blue-50"
                >
                  Sauvegarder
                </Button>
                <Button
                  type="button"
                  onClick={handleReset}
                  className="bg-white border border-orange-500 text-orange-500 rounded hover:bg-orange-50"
                >
                  Réinitialiser
                </Button>
                <Button
                  type="button"
                  onClick={() => setShowPreview(true)}
                  className="bg-white border border-purple-500 text-purple-500 rounded hover:bg-purple-50"
                >
                  Prévisualiser
                </Button>
              </div>
              <Button
                type="submit"
                className="bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700 font-medium"
              >
                Suivant
              </Button>
            </div>

          </form>
        </div>
      </div>

      {/* Modal prévisualisation */}
      {showPreview && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg max-w-2xl w-full max-h-[80vh] overflow-auto">
            <h3 className="text-2xl font-bold mb-4">Prévisualisation — Informations Client</h3>
            <div className="space-y-2 text-sm">
              <p><span className="font-bold">Nom du client :</span> {formData.clientName}</p>
              <p><span className="font-bold">Pays :</span> {formData.region}</p>
              <p><span className="font-bold">À l'attention de :</span> {formData.attentionTitle} {formData.attentionName}</p>
              <p><span className="font-bold">Mobile :</span> {formData.mobile}</p>
              <p><span className="font-bold">Téléphone fixe :</span> {formData.phone || '—'}</p>
              <p><span className="font-bold">Email :</span> {formData.email || '—'}</p>
              {(formData.emailsCc || []).length > 0 && (
                <p>
                  <span className="font-bold">Emails CC :</span>{' '}
                  {formData.emailsCc.join(', ')}
                </p>
              )}
              <p><span className="font-bold">Ville / Localité :</span> {formData.address}</p>
              <p><span className="font-bold">Numéro PO :</span> {formData.poNumber}</p>
            </div>
            <Button onClick={() => setShowPreview(false)} className="mt-4 bg-gray-600 text-white">
              Fermer
            </Button>
          </div>
        </div>
      )}
    </motion.div>
    <ScrollToTop />
    </>
  );
};

export default ClientInfo;
